import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

dayjs.extend(utc)
dayjs.extend(timezone)

export const getClockNumber = (num: string | number): string | undefined => {
  const isString = (typeof num === 'string')
  if (isString) {
    let value = parseInt(num, 10)
    value = (value < 0) ? 0 : value

    if (isNaN(value)) {
      return undefined
    } else {
      return (value < 10)
        ? `0${value}`
        : `${value}`
    }
  } else {
    let value = (num < 0) ? 0 : num

    if (!Number.isInteger(value)) {
      value = Math.trunc(value)
    }
    return (value < 10)
      ? `0${value}`
      : `${value}`
  }
}

export const formatBonusClock = (totalSeconds: number): string => {
  const total = Math.max(0, Math.floor(totalSeconds))
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const seconds = total % 60
  const hh = hours > 0 ? `${getClockNumber(hours)}:` : ''
  return `${hh}${getClockNumber(minutes)}:${getClockNumber(seconds)}`
}

export const formatDate = (v: string, format: DateFormat): string => {
  return dayjs(v, DateFormat.Default, 'en', true).format(format)
}

export const formatIsoDate = (v: string, format: DateFormat): string => {
  return dayjs(v).format(format)
}

export const formatDefaultDate = (v: string): string => formatDate(v, DateFormat.DefaultDate)
export const formatFileDate = (v: string): string => formatDate(v, DateFormat.FileDate)
export const formatBookedDate = (v: string): string => formatDate(v, DateFormat.BookedDate)
export const formatBookedTime = (v: string): string => formatDate(v, DateFormat.BookedTime)
export const formatBookedSlot = (v: string): string => formatDate(v, DateFormat.BookedSlot)
export const formatTimeAmPm = (v: string): string => formatDate(v, DateFormat.AmPm)
export const formatSlotTime = (v: string): string => formatDate(v, DateFormat.SlotTime)
export const formatBookedFullDate = (v: string): string => formatDate(v, DateFormat.BookedFullDate)

const SCHEDULE_TIME_FORMAT = 'h A'

export const formatScheduleTime = (v: string): string => dayjs(v, DateFormat.Default, 'en', true).format(SCHEDULE_TIME_FORMAT)
export const formatSummaryDate = (v: string): string => formatDate(v, DateFormat.SummaryDate)

export const getDateChipLabels = (date: string): { title: string, description: string } => {
  const named: Record<string, string> = {
    [dayjs().format(DateFormat.DefaultDate)]: 'Today',
    [dayjs().add(1, 'day').format(DateFormat.DefaultDate)]: 'Tomorrow',
  }

  return { title: named[date] ?? dayjs(date).format('ddd'), description: dayjs(date).format('MMM D') }
}

export const formatSecondsToClockTimer = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds - (hours * 3600)) / 60)
  const remainingSeconds = seconds - (hours * 3600) - (minutes * 60)

  const formattedMinutes = minutes.toString().padStart(2, '0')
  const formattedSeconds = remainingSeconds.toString().padStart(2, '0')

  if (hours > 0) {
    const formattedHours = hours.toString().padStart(2, '0')
    return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`
  } else {
    return `${formattedMinutes}:${formattedSeconds}`
  }
}

export const isToday = (date: string, currentTime: string): boolean => dayjs(date).isSame(dayjs(currentTime), 'day')
